/**
 * 🔒 SECURITY: CSRF Token Utilities
 * Centralized helpers for reading and applying CSRF tokens across HTTP clients
 * 
 * Usage:
 * - Axios: Use withCsrfHeader() in request interceptor
 * - Fetch: Use csrfFetch() wrapper instead of native fetch
 */

/**
 * Read CSRF token from XSRF-TOKEN cookie
 * Returns null in SSR context or if cookie not found
 */
export function getCsrfToken(): string | null {
  // SSR guard
  if (typeof document === 'undefined') return null;
  
  const name = 'XSRF-TOKEN=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    // Trim leading spaces
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    // Check if this cookie is XSRF-TOKEN
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  
  return null;
}

/**
 * Add CSRF token to headers object for state-changing requests
 * Used by Axios interceptor
 */
export function withCsrfHeader(headers: Record<string, string> = {}): Record<string, string> {
  const csrfToken = getCsrfToken();
  
  if (csrfToken) {
    headers['x-xsrf-token'] = csrfToken;
  }
  
  return headers;
}

/**
 * Fetch wrapper that automatically includes CSRF token for POST/PUT/PATCH/DELETE
 * Drop-in replacement for native fetch()
 * 
 * @example
 * const response = await csrfFetch('/api/endpoint', {
 *   method: 'POST',
 *   credentials: 'include',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify(data)
 * });
 */
export async function csrfFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: { skipClientContext?: boolean }
): Promise<Response> {
  const method = init?.method?.toUpperCase() || 'GET';
  const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  const csrfToken = needsCsrf ? getCsrfToken() : null;
  const selectedClientId = !options?.skipClientContext && typeof window !== 'undefined' && window.localStorage
    ? localStorage.getItem('adminSelectedClientId')
    : null;

  if (csrfToken || selectedClientId) {
    const headers = new Headers(init?.headers);
    if (csrfToken) headers.set('x-xsrf-token', csrfToken);
    // An action captures its tenant when it starts. A later picker change must
    // not silently redirect that action to another client's configuration.
    if (selectedClientId && !headers.has('x-client-context')) {
      headers.set('x-client-context', selectedClientId);
    }
    init = {
      ...init,
      headers: Object.fromEntries(headers.entries())
    };
  }
  
  return fetch(input, init);
}
